import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Cookie, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCookieConsentStore } from '@/stores/cookieConsentStore';
import { cn } from '@/lib/utils';

export function CookieBanner() {
  const { t } = useTranslation();
  const { hasConsented, preferences, acceptAll, acceptEssentialOnly, savePreferences } =
    useCookieConsentStore();
  const [showDetails, setShowDetails] = useState(false);
  const [localPrefs, setLocalPrefs] = useState(preferences);

  useEffect(() => { setLocalPrefs(preferences); }, [preferences]);

  // Don't render if user has already consented
  if (hasConsented) {
    return null;
  }

  const handleToggle = (key: 'analytics' | 'marketing') => {
    setLocalPrefs((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSavePreferences = () => {
    savePreferences(localPrefs);
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6">
      <div
        className={cn(
          'pointer-events-auto mx-auto max-w-4xl max-h-[85vh] overflow-y-auto rounded-2xl border border-neutral-200 bg-white shadow-2xl',
          'dark:border-neutral-700 dark:bg-neutral-800',
          'animate-in slide-in-from-bottom-5 duration-500'
        )}
      >
        {/* Main banner content */}
        <div className="p-4 sm:p-6">
          <div className="flex items-start gap-4">
            {/* Cookie icon */}
            <div className="hidden shrink-0 sm:block">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
                <Cookie className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
            </div>

            {/* Text content */}
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                {t('cookies.title', 'We value your privacy')}
              </h3>
              <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">
                {t(
                  'cookies.description',
                  'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.'
                )}
              </p>

              {/* Expand/collapse details */}
              <button
                onClick={() => setShowDetails(!showDetails)}
                aria-expanded={showDetails}
                className="mt-2 flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                {showDetails
                  ? t('cookies.hideDetails', 'Hide details')
                  : t('cookies.showDetails', 'Customize preferences')}
                {showDetails ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>
            </div>

            {/* Close button - only rejects non-essential */}
            <button
              onClick={acceptEssentialOnly}
              className="shrink-0 rounded-lg p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-500 dark:hover:bg-neutral-700 dark:hover:text-neutral-300"
              title={t('cookies.rejectNonEssential', 'Reject non-essential cookies')}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Expandable cookie preferences */}
          {showDetails && (
            <div className="mt-4 space-y-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-700 dark:bg-neutral-900/50">
              {/* Essential cookies - always on */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-neutral-900 dark:text-white">
                    {t('cookies.essential', 'Essential')}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    {t(
                      'cookies.essentialDescription',
                      'Required for the application to function properly'
                    )}
                  </p>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    aria-label={t('cookies.essential')}
                    checked
                    disabled
                    className="h-5 w-9 cursor-not-allowed appearance-none rounded-full bg-primary-600 after:absolute after:left-[18px] after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:shadow after:transition-all"
                  />
                </div>
              </div>

              {/* Analytics cookies */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-neutral-900 dark:text-white">
                    {t('cookies.analytics', 'Analytics')}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    {t(
                      'cookies.analyticsDescription',
                      'Help us understand how visitors interact with our app'
                    )}
                  </p>
                </div>
                <button
                  onClick={() => handleToggle('analytics')}
                  role="switch" aria-checked={localPrefs.analytics} aria-label={t('cookies.analytics')}
                  className={cn(
                    'relative h-6 w-11 rounded-full transition-colors duration-200',
                    localPrefs.analytics
                      ? 'bg-primary-600'
                      : 'bg-neutral-300 dark:bg-neutral-600'
                  )}
                >
                  <span
                    className={cn(
                      'absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200',
                      localPrefs.analytics && 'translate-x-5'
                    )}
                  />
                </button>
              </div>

            </div>
          )}

          {/* Action buttons */}
          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={acceptEssentialOnly} className="sm:w-auto">
              {t('cookies.essentialOnly', 'Essential Only')}
            </Button>
            {showDetails ? (
              <Button onClick={handleSavePreferences} className="sm:w-auto">
                {t('cookies.savePreferences', 'Save Preferences')}
              </Button>
            ) : (
              <Button onClick={acceptAll} className="sm:w-auto">
                {t('cookies.acceptAll', 'Accept All')}
              </Button>
            )}
          </div>

          {/* Privacy policy link */}
          <p className="mt-3 text-center text-xs text-neutral-500 dark:text-neutral-400 sm:text-left">
            {t('cookies.learnMore', 'Learn more about how we use cookies in our')}{' '}
            <a
              href={t('publicPrivacyPath', '/en/privacy')}
              className="font-medium text-primary-600 hover:underline dark:text-primary-400"
            >
              {t('cookies.privacyPolicy', 'Privacy Policy')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
